import React, { useEffect, useState, useRef } from 'react';
import {
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  useDisclosure
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../../contexts/SocketContext.tsx';
import { SoundControl } from '../Sound/SoundControl.tsx';
import CategorySelector from '../Game/CategorySelector.tsx';
import { Category } from '../../types/game.ts';
import PlayerName from '../Common/PlayerName.tsx';
import './LobbyRoomPage.css';

const PLAYER_COLORS = [
  '#4F8CFF', '#FFB347', '#FF6961', '#77DD77', '#C299FC', '#FFD700'
];

// Avoid relying on Node's process types in the browser
// @ts-ignore
const API_URL = process.env.REACT_APP_API_URL || '';

const defaultRules = {
  numPlayers: 2,
  playerNames: ['Joueur 1', 'Joueur 2'],
  streakToWin: 5,
  categoriesToWin: 3,
  difficulty: [1, 5] as [number, number],
  difficultyMode: 'random' as 'random' | 'adaptive' | 'normal',
  selectedCategories: [] as Category[], // Ajout pour les catégories sélectionnées
  mysteryAttackMode: 'roulette' as 'classic' | 'roulette', // Mode d'attaque mystère
  // Configuration avancée
  classicMalusValue: 1, // Valeur du malus en mode classique
  // Cooldown Mystère
  mysteryCooldownEnabled: true,
  mysteryCooldownTurns: 3,
  // Timers (désactivés par défaut)
  enableCategoryTimer: false,
  categoryTimerSeconds: 15,
  enableQuestionTimer: false,
  questionTimerSeconds: 20,
  // Anti-triche (activé par défaut)
  enableAntiCheat: true,
  roulettePowers: {
  // Pouvoirs Malus (Bleu)
  minus1: { enabled: true, chance: 24 },
  minus2: { enabled: true, chance: 16 },
  minus3: { enabled: true, chance: 10 },
  // Pouvoirs Bonus (Vert)
  plus1: { enabled: true, chance: 18 },
  plus2: { enabled: true, chance: 12 },
  plus3: { enabled: true, chance: 8 },
  // Pouvoirs Swap (Orange)
  swap1: { enabled: true, chance: 20 },
  swap2: { enabled: true, chance: 14 },
  swap3: { enabled: true, chance: 10 },
  // Pouvoirs Spéciaux
  blocked:   { enabled: true, chance: 10 },
  nuke:      { enabled: true, chance: 4 },
  blind:     { enabled: true, chance: 10 },
  megaswap:  { enabled: true, chance: 6 },
  translate: { enabled: true, chance: 10 },
  hardcore:  { enabled: true, chance: 8 }
  }
};

const LobbyRoomPage = ({ user, lobbyCode, isOwner: initialIsOwner, onLeave, onStartMultiplayerGame }) => {
  const { t } = useTranslation();
  const [players, setPlayers] = useState<any[]>([]);
  const [rules, setRules] = useState({ ...defaultRules });
  const [isOwner, setIsOwner] = useState(initialIsOwner); // État local pour gérer les changements de propriétaire
  const [copied, setCopied] = useState(false);
  const [kickedModal, setKickedModal] = useState(false);
  const [lobbyNotFoundModal, setLobbyNotFoundModal] = useState(false); // Nouvel état pour lobby inexistant
  const [countdown, setCountdown] = useState(3); // État pour le countdown
  const [skipAutoReconnect, setSkipAutoReconnect] = useState(false); // Éviter la reconnexion automatique après reset
  const [codeBlurred, setCodeBlurred] = useState(true);
  const socketContext = useSocket();
  const [isModifyingRules, setIsModifyingRules] = useState(false);

  // Fonction pour quitter le lobby proprement
  const handleLeaveClick = () => {
    // Déconnecter le socket pour déclencher l'événement 'disconnect' côté serveur
    // qui retirera le joueur du lobby automatiquement
    if (socketContext && socketContext.socket) {
      socketContext.socket.disconnect();
      // Reconnecter immédiatement pour permettre de rejoindre un autre lobby
      setTimeout(() => {
        if (socketContext && socketContext.socket) {
          socketContext.socket.connect();
        }
      }, 100);
    }
    // Appeler la fonction onLeave fournie par le parent
    onLeave();
  };
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const temporaryRevealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rulesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // État pour le CategorySelector - synchronisé avec rules.selectedCategories
  const { isOpen: isCategorySelectorOpen, onOpen: onCategorySelectorOpen, onClose: onCategorySelectorClose } = useDisclosure();
  
  // Utiliser rules.selectedCategories comme source de vérité
  const selectedCategories = rules.selectedCategories || [];

  // Met à jour les règles avec les catégories sélectionnées
  const handleCategoriesSelected = async (categories: Category[]) => {
    console.log('🎯 Catégories sélectionnées par l\'utilisateur:', categories);
    
    const updatedRules = {
      ...rules,
      selectedCategories: categories
    };
    setRules(updatedRules);
    
    // Sauvegarder en base de données immédiatement
    try {
      await fetch(`${API_URL}/api/lobby/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: lobbyCode, rules: updatedRules }),
      });
      console.log('✅ Catégories sauvegardées en base:', categories);
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde des catégories:', error);
    }
    
    // Synchronise avec les autres joueurs via socket
    if (socketContext?.socket && isOwner) {
      socketContext.socket.emit('update-lobby-rules', { 
        lobbyCode, 
        rules: updatedRules 
      });
      console.log('📡 Règles diffusées via socket:', updatedRules);
    }
  };

  // Gestion du socket et des événements du lobby

  // Effet pour détecter et gérer la reconnexion automatique
  useEffect(() => {
    // Vérifier s'il y a une partie en cours à laquelle on peut se reconnecter
    // Mais éviter la reconnexion si on vient de faire un retour au lobby
    if (socketContext?.socket && user?.displayName && !skipAutoReconnect) {
      console.log('🔄 Tentative de reconnexion automatique...');
      socketContext.reconnectToGame(lobbyCode, user.displayName);
    } else if (skipAutoReconnect) {
      console.log('⏭️ Reconnexion automatique ignorée (retour au lobby récent)');
    }
  }, [socketContext?.socket, lobbyCode, user?.displayName, skipAutoReconnect]);

  // Gérer les événements de connexion/déconnexion des joueurs
  useEffect(() => {
    if (!socketContext) return;

    const handlePlayerDisconnected = ({ playerName, playerIndex }: { playerName: string; playerIndex: number }) => {
      console.log(`📤 Joueur ${playerName} déconnecté`);
      // Mettre à jour la liste des joueurs pour marquer le joueur comme déconnecté
      setPlayers(prev => prev.map((p, index) => 
        index === playerIndex ? { ...p, isConnected: false } : p
      ));
    };

    const handlePlayerReconnected = ({ playerName, playerIndex }: { playerName: string; playerIndex: number }) => {
      console.log(`📥 Joueur ${playerName} reconnecté`);
      // Remettre le joueur comme connecté
      setPlayers(prev => prev.map((p, index) => 
        index === playerIndex ? { ...p, isConnected: true } : p
      ));
    };

    const handleGameReconnected = (gameData: any) => {
      console.log('🎮 Reconnexion à la partie en cours détectée:', gameData);
      if (onStartMultiplayerGame) {
        const multiplayerData = {
          lobbyCode: gameData.lobbyCode,
          players: gameData.players.map((player: any, index: number) => ({
            id: index + 1,
            name: player.name,
            uid: player.uid || ''
          })),
          rules: gameData.rules,
          currentUser: user,
          hostName: gameData.hostName
        };
        onStartMultiplayerGame(multiplayerData);
      }
    };

    const handleLobbyOwnerChanged = ({ newOwner }: { newOwner: string }) => {
      console.log(`👑 Nouveau propriétaire du lobby: ${newOwner}`);
      console.log(`👑 Utilisateur actuel: ${user.displayName}`);
      // Mettre à jour le statut isOwner local
      const newIsOwner = newOwner === user.displayName;
      console.log(`👑 Nouveau statut isOwner: ${newIsOwner}`);
      setIsOwner(newIsOwner);
    };

    const handleGameResetToLobby = (data: { lobbyCode: string; message: string }) => {
      console.log('🏠 Partie réinitialisée, éviter la reconnexion automatique:', data);
      // Éviter la reconnexion automatique pendant 5 secondes
      setSkipAutoReconnect(true);
      setTimeout(() => {
        setSkipAutoReconnect(false);
        console.log('✅ Reconnexion automatique ré-autorisée');
      }, 5000);
    };

    // Écouter les événements
    socketContext.onPlayerDisconnected(handlePlayerDisconnected);
    socketContext.onPlayerReconnected(handlePlayerReconnected);
    socketContext.onGameReconnected(handleGameReconnected);
    socketContext.onLobbyOwnerChanged(handleLobbyOwnerChanged);
    socketContext.onGameResetToLobby(handleGameResetToLobby);

    // Nettoyage
    return () => {
      socketContext.offPlayerDisconnected(handlePlayerDisconnected);
      socketContext.offPlayerReconnected(handlePlayerReconnected);
      socketContext.offGameReconnected(handleGameReconnected);
      socketContext.offLobbyOwnerChanged(handleLobbyOwnerChanged);
      socketContext.offGameResetToLobby(handleGameResetToLobby);
    };
  }, [socketContext, user.displayName, onStartMultiplayerGame, lobbyCode]);

  // Récupère la liste des joueurs et règles du lobby
  useEffect(() => {
    const mergeRoulettePowers = (incoming: any) => ({
      ...defaultRules.roulettePowers,
      ...(incoming || {})
    });

    const fetchLobby = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lobby/${lobbyCode}`);
        
        if (!res.ok) {
          console.error('❌ Lobby non trouvé, redirection vers l\'accueil');
          setLobbyNotFoundModal(true);
          setCountdown(3);
          
          // Countdown et redirection
          const countdownInterval = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                onLeave();
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          
          return;
        }
        
        const data = await res.json();
        
        console.log(`🔍 Données du lobby reçues:`, data);
        console.log(`🔍 Host du lobby: ${data.hostName}`);
        console.log(`🔍 Utilisateur actuel: ${user.displayName}`);
        console.log(`🔍 isOwner initial: ${initialIsOwner}`);
        console.log(`🔍 isOwner actuel: ${isOwner}`);
        
        // Vérifier et mettre à jour le statut isOwner
        const actualIsOwner = data.hostName === user.displayName;
        console.log(`🔍 Statut isOwner calculé: ${actualIsOwner}`);
        
        if (actualIsOwner !== isOwner) {
          console.log(`👑 Mise à jour du statut isOwner: ${isOwner} → ${actualIsOwner}`);
          setIsOwner(actualIsOwner);
        }
        
        // Vérifier si le joueur est toujours dans le lobby
        const playerNames = Array.isArray(data.players) ? 
          data.players.map((p: any) => typeof p === 'string' ? p : p.name) : [];
        
        if (playerNames.length > 0 && !playerNames.includes(user.displayName)) {
          setKickedModal(true);
          return;
        }
        
        setPlayers(data.players || []);
        
        // Vérifier si une partie a commencé
    if (data.gameStarted && onStartMultiplayerGame) {
          console.log('🎮 Partie déjà démarrée, redirection automatique');
          const multiplayerData = {
            lobbyCode,
            players: data.players.map((player: any, index: number) => ({
              id: index + 1,
              name: typeof player === 'string' ? player : player.name,
              uid: typeof player === 'string' ? '' : (player.uid || '')
            })),
            rules: {
              streakToWin: data.rules?.streakToWin || rules.streakToWin,
              categoriesToWin: data.rules?.categoriesToWin || rules.categoriesToWin,
              difficulty: data.rules?.difficulty || rules.difficulty,
              difficultyMode: data.rules?.difficultyMode || rules.difficultyMode || 'random',
              selectedCategories: data.rules?.selectedCategories || selectedCategories,
              mysteryAttackMode: data.rules?.mysteryAttackMode || 'classic',
              classicMalusValue: data.rules?.classicMalusValue || 1,
      enableCategoryTimer: data.rules?.enableCategoryTimer ?? rules.enableCategoryTimer ?? false,
      categoryTimerSeconds: data.rules?.categoryTimerSeconds ?? rules.categoryTimerSeconds ?? 15,
      enableQuestionTimer: data.rules?.enableQuestionTimer ?? rules.enableQuestionTimer ?? false,
      questionTimerSeconds: data.rules?.questionTimerSeconds ?? rules.questionTimerSeconds ?? 20,
              enableAntiCheat: data.rules?.enableAntiCheat ?? rules.enableAntiCheat ?? true,
              roulettePowers: mergeRoulettePowers(data.rules?.roulettePowers)
              ,
              mysteryCooldownEnabled: data.rules?.mysteryCooldownEnabled ?? rules.mysteryCooldownEnabled ?? true,
              mysteryCooldownTurns: data.rules?.mysteryCooldownTurns ?? rules.mysteryCooldownTurns ?? 3
            },
            currentUser: user,
            hostName: data.owner || data.players[0] // Utiliser owner ou le premier joueur comme fallback
          };
          onStartMultiplayerGame(multiplayerData);
          return;
        }
        
        // Ne mettre à jour les règles que si l'utilisateur n'est pas en train de les modifier
    if (data.rules && !isModifyingRules) {
          console.log('🔄 Récupération des règles depuis le serveur:', data.rules);
          console.log('🔄 Catégories dans les règles serveur:', data.rules.selectedCategories);
          
      setRules(prevRules => {
            const serverRules = {
              ...defaultRules,
      ...data.rules,
      roulettePowers: mergeRoulettePowers(data.rules.roulettePowers)
            };
            console.log('🔄 Règles finales appliquées:', serverRules);
            return {
              ...serverRules,
        difficultyMode: serverRules.difficultyMode || 'random',
              difficulty: Array.isArray(serverRules.difficulty)
                ? serverRules.difficulty
                : [Number(serverRules.difficulty) || 1, 5]
            };
          });
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des infos du lobby:", error);
      }
    };
    
    fetchLobby();
    // Réduire la fréquence des appels API de 2s à 5s pour éviter le spam
    const interval = setInterval(fetchLobby, 2000);
    
    // Connecter automatiquement au lobby WebSocket
    if (socketContext && socketContext.socket) {
      socketContext.socket.emit('join-lobby', { lobbyCode, playerName: user.displayName, uid: user?.uid });
    }
    
    // Fonction pour gérer la mise à jour des règles en temps réel
  const handleLobbyRulesUpdated = ({ rules: newRules }) => {
      console.log('⚙️ Règles mises à jour via WebSocket:', newRules);
      console.log('⚙️ Catégories dans les nouvelles règles:', newRules.selectedCategories);
      if (!isModifyingRules) {
    setRules(prevRules => {
          const serverRules = {
            ...defaultRules,
      ...newRules,
      roulettePowers: mergeRoulettePowers(newRules.roulettePowers)
  ,
  mysteryCooldownEnabled: newRules?.mysteryCooldownEnabled ?? rules.mysteryCooldownEnabled ?? true,
  mysteryCooldownTurns: newRules?.mysteryCooldownTurns ?? rules.mysteryCooldownTurns ?? 3
          };
          console.log('⚙️ Règles finales appliquées via socket:', serverRules);
          return {
            ...serverRules,
      difficultyMode: serverRules.difficultyMode || 'random',
            difficulty: Array.isArray(serverRules.difficulty)
              ? serverRules.difficulty
              : [Number(serverRules.difficulty) || 1, 5]
          };
        });
      }
    };
    
    // Fonction pour gérer la redirection vers la partie multijoueur
    const handleRedirectToMultiplayerGame = (payload: { lobbyCode: string; players: any[]; rules?: any }) => {
  const { lobbyCode: gameLobbyCode, players: gamePlayers, rules: gameRules } = payload || ({} as any);
      console.log('🎮 Redirection vers la partie multijoueur:', { gameLobbyCode, gamePlayers, gameRules });
      console.log('🎮 Règles reçues du serveur:', gameRules);
      console.log('🎮 Catégories sélectionnées reçues du serveur:', gameRules?.selectedCategories);
      
      if (onStartMultiplayerGame) {
        const multiplayerData = {
          lobbyCode: gameLobbyCode,
          players: gamePlayers.map((player: any, index: number) => ({
            id: index + 1,
            name: typeof player === 'string' ? player : player.name,
            uid: typeof player === 'string' ? '' : (player.uid || '')
          })),
          rules: {
            streakToWin: gameRules?.streakToWin || rules.streakToWin,
            categoriesToWin: gameRules?.categoriesToWin || rules.categoriesToWin,
            difficulty: gameRules?.difficulty || rules.difficulty,
            difficultyMode: gameRules?.difficultyMode || rules.difficultyMode || 'random',
            selectedCategories: gameRules?.selectedCategories || rules.selectedCategories,
            mysteryAttackMode: gameRules?.mysteryAttackMode || rules.mysteryAttackMode || 'classic',
            classicMalusValue: gameRules?.classicMalusValue || rules.classicMalusValue || 1,
            enableCategoryTimer: gameRules?.enableCategoryTimer ?? rules.enableCategoryTimer ?? false,
            categoryTimerSeconds: gameRules?.categoryTimerSeconds ?? rules.categoryTimerSeconds ?? 15,
            enableQuestionTimer: gameRules?.enableQuestionTimer ?? rules.enableQuestionTimer ?? false,
            questionTimerSeconds: gameRules?.questionTimerSeconds ?? rules.questionTimerSeconds ?? 20,
  enableAntiCheat: gameRules?.enableAntiCheat ?? rules.enableAntiCheat ?? true,
  roulettePowers: mergeRoulettePowers(gameRules?.roulettePowers || rules.roulettePowers),
  mysteryCooldownEnabled: gameRules?.mysteryCooldownEnabled ?? rules.mysteryCooldownEnabled ?? true,
  mysteryCooldownTurns: gameRules?.mysteryCooldownTurns ?? rules.mysteryCooldownTurns ?? 3
          },
          currentUser: user,
          hostName: gamePlayers[0] // Premier joueur comme fallback pour l'hôte
        };
        
        console.log('🎮 Données multijoueur envoyées:', multiplayerData);
        onStartMultiplayerGame(multiplayerData);
      }
    };
    
    // Écouter les événements WebSocket
    if (socketContext) {
      socketContext.onLobbyRulesUpdated(handleLobbyRulesUpdated);
      socketContext.onRedirectToMultiplayerGame(handleRedirectToMultiplayerGame);
    }
    
    return () => {
      clearInterval(interval);
      // Nettoyage des timeouts
      if (temporaryRevealTimeoutRef.current) {
        clearTimeout(temporaryRevealTimeoutRef.current);
      }
      if (rulesTimeoutRef.current) {
        clearTimeout(rulesTimeoutRef.current);
      }
      // Nettoyage des événements socket
      if (socketContext) {
        socketContext.offLobbyRulesUpdated(handleLobbyRulesUpdated);
        socketContext.offRedirectToMultiplayerGame(handleRedirectToMultiplayerGame);
      }
    };
  }, [lobbyCode, user.displayName, isOwner, socketContext, onStartMultiplayerGame, isModifyingRules]); // Retrait de 'rules' et 'user' des dépendances pour éviter la boucle

  // Nettoyage à la destruction du composant
  useEffect(() => {
    return () => {
      if (temporaryRevealTimeoutRef.current) {
        clearTimeout(temporaryRevealTimeoutRef.current);
      }
      if (rulesTimeoutRef.current) {
        clearTimeout(rulesTimeoutRef.current);
      }
    };
  }, []);

  // Copier le lien d'invitation
  const handleCopy = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${lobbyCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Gérer la visibilité du code pour les streamers
  const toggleCodeVisibility = () => {
    if (codeBlurred) {
      setCodeBlurred(false);
      if (temporaryRevealTimeoutRef.current) {
        clearTimeout(temporaryRevealTimeoutRef.current);
      }
      temporaryRevealTimeoutRef.current = setTimeout(() => {
        setCodeBlurred(true);
        temporaryRevealTimeoutRef.current = null;
      }, 3000);
    } else {
      setCodeBlurred(true);
      if (temporaryRevealTimeoutRef.current) {
        clearTimeout(temporaryRevealTimeoutRef.current);
        temporaryRevealTimeoutRef.current = null;
      }
    }
  };

  // Virer un joueur (seulement pour le créateur)
  const handleKick = async (playerName: string) => {
    await fetch(`${API_URL}/api/lobby/kick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: lobbyCode, playerName }),
    });
    setPlayers(players.filter(p => p !== playerName));
  };

  // Modifier les règles (pour le créateur)
  const handleRuleChange = (e) => {
    setIsModifyingRules(true);
    const { name, value } = e.target;
    
    let newRules = { ...rules };
    
    if (name === 'numPlayers') {
      const num = Math.max(2, Math.min(6, Number(value)));
      newRules = {
        ...newRules,
        numPlayers: num,
        playerNames: newRules.playerNames.slice(0, num).concat(Array(num - newRules.playerNames.length).fill('').map((_, i) => `Joueur ${newRules.playerNames.length + i + 1}`)),
      };
    } else if (name.startsWith('playerName')) {
      const idx = Number(name.replace('playerName', ''));
      const newNames = [...newRules.playerNames];
      newNames[idx] = value;
      newRules = { ...newRules, playerNames: newNames };
    } else if (name === 'streakToWin' || name === 'categoriesToWin') {
      newRules = { ...newRules, [name]: Number(value) };
    } else if (name === 'mysteryAttackMode') {
      newRules = { ...newRules, mysteryAttackMode: value };
    }
    
    setRules(newRules);
    
    // Annule le timeout précédent s'il existe
    if (rulesTimeoutRef.current) {
      clearTimeout(rulesTimeoutRef.current);
    }
    
    // Sauvegarde les règles après un délai
    rulesTimeoutRef.current = setTimeout(async () => {
      try {
        // Sauvegarder en base de données
        await fetch(`${API_URL}/api/lobby/rules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: lobbyCode, rules: newRules }),
        });
        
        // Diffuser les changements via WebSocket
        if (socketContext) {
          socketContext.updateLobbyRules(lobbyCode, newRules);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des règles:', error);
      } finally {
        setIsModifyingRules(false);
      }
    }, 1000);
  };

  const handleDifficultyChange = (val: [number, number]) => {
    setIsModifyingRules(true);
    const newRules = { ...rules, difficulty: val };
    setRules(newRules);
    
    // Annule le timeout précédent s'il existe
    if (rulesTimeoutRef.current) {
      clearTimeout(rulesTimeoutRef.current);
    }
    
    // Sauvegarde les règles après un délai
    rulesTimeoutRef.current = setTimeout(async () => {
      try {
        // Sauvegarder en base de données
        await fetch(`${API_URL}/api/lobby/rules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: lobbyCode, rules: newRules }),
        });
        
        // Diffuser les changements via WebSocket
        if (socketContext) {
          socketContext.updateLobbyRules(lobbyCode, newRules);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la difficulté:', error);
      } finally {
        setIsModifyingRules(false);
      }
    }, 500);
  };

  // Gérer le changement du malus classique
  const handleClassicMalusChange = (e) => {
    setIsModifyingRules(true);
    const value = Math.max(1, Math.min(5, Number(e.target.value))); // Limiter entre 1 et 5
    const newRules = { ...rules, classicMalusValue: value };
    setRules(newRules);
    
    // Sauvegarde différée
    if (rulesTimeoutRef.current) {
      clearTimeout(rulesTimeoutRef.current);
    }
    
    rulesTimeoutRef.current = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/lobby/rules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: lobbyCode, rules: newRules }),
        });
        
        if (socketContext) {
          socketContext.updateLobbyRules(lobbyCode, newRules);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du malus classique:', error);
      } finally {
        setIsModifyingRules(false);
      }
    }, 500);
  };

  // Sauvegarde générique (debounce) des règles
  const saveRulesDebounced = (newRules, delay = 500) => {
    if (rulesTimeoutRef.current) {
      clearTimeout(rulesTimeoutRef.current);
    }
    rulesTimeoutRef.current = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/lobby/rules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: lobbyCode, rules: newRules }),
        });
        if (socketContext) {
          socketContext.updateLobbyRules(lobbyCode, newRules);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des règles (debounced):', error);
      } finally {
        setIsModifyingRules(false);
      }
    }, delay);
  };

  // Gestion des timers (UI -> règles)
  const handleCategoryTimerToggle = (e) => {
    if (!isOwner) return;
    setIsModifyingRules(true);
    const newRules = { ...rules, enableCategoryTimer: !!e.target.checked };
    setRules(newRules);
    saveRulesDebounced(newRules, 400);
  };

  const handleCategoryTimerSecondsChange = (e) => {
    if (!isOwner) return;
    setIsModifyingRules(true);
    const value = Math.max(5, Math.min(300, Number(e.target.value) || 0));
    const newRules = { ...rules, categoryTimerSeconds: value };
    setRules(newRules);
    saveRulesDebounced(newRules, 500);
  };

  const handleQuestionTimerToggle = (e) => {
    if (!isOwner) return;
    setIsModifyingRules(true);
    const newRules = { ...rules, enableQuestionTimer: !!e.target.checked };
    setRules(newRules);
    saveRulesDebounced(newRules, 400);
  };

  const handleQuestionTimerSecondsChange = (e) => {
    if (!isOwner) return;
    setIsModifyingRules(true);
    const value = Math.max(5, Math.min(300, Number(e.target.value) || 0));
    const newRules = { ...rules, questionTimerSeconds: value };
    setRules(newRules);
    saveRulesDebounced(newRules, 500);
  };

  // Anti-triche: activer/désactiver
  const handleAntiCheatToggle = (e) => {
    if (!isOwner) return;
    setIsModifyingRules(true);
    const newRules = { ...rules, enableAntiCheat: !!e.target.checked };
    setRules(newRules);
    saveRulesDebounced(newRules, 400);
  };

  // Mystère: cooldown enable/disable
  const handleMysteryCooldownToggle = (e) => {
    if (!isOwner) return;
    setIsModifyingRules(true);
    const newRules = { ...rules, mysteryCooldownEnabled: !!e.target.checked };
    setRules(newRules);
    saveRulesDebounced(newRules, 400);
  };

  // Mystère: cooldown turns
  const handleMysteryCooldownTurnsChange = (e) => {
    if (!isOwner) return;
    setIsModifyingRules(true);
    const value = Math.max(0, Math.min(10, Number(e.target.value) || 0));
    const newRules = { ...rules, mysteryCooldownTurns: value };
    setRules(newRules);
    saveRulesDebounced(newRules, 500);
  };

  // Mode de difficulté: random | adaptive | normal
  const handleDifficultyModeChange = (e) => {
    if (!isOwner) return;
    setIsModifyingRules(true);
    const mode = (e.target?.value || 'random') as 'random' | 'adaptive' | 'normal';
    const newRules = { ...rules, difficultyMode: mode };
    setRules(newRules);
    saveRulesDebounced(newRules, 500);
  };

  // Gérer l'activation/désactivation des pouvoirs
  const handlePowerToggle = (powerId: string) => {
    setIsModifyingRules(true);
    const newRules = {
      ...rules,
      roulettePowers: {
        ...rules.roulettePowers,
        [powerId]: {
          ...rules.roulettePowers[powerId],
          enabled: !rules.roulettePowers[powerId].enabled
        }
      }
    };
    setRules(newRules);
    
    // Sauvegarde différée
    if (rulesTimeoutRef.current) {
      clearTimeout(rulesTimeoutRef.current);
    }
    
    rulesTimeoutRef.current = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/lobby/rules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: lobbyCode, rules: newRules }),
        });
        
        if (socketContext) {
          socketContext.updateLobbyRules(lobbyCode, newRules);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des pouvoirs:', error);
      } finally {
        setIsModifyingRules(false);
      }
    }, 500);
  };

  // Gérer le changement de pourcentage des pouvoirs
  const handlePowerChanceChange = (powerId: string, chance: number) => {
    setIsModifyingRules(true);
    const newRules = {
      ...rules,
      roulettePowers: {
        ...rules.roulettePowers,
        [powerId]: {
          ...rules.roulettePowers[powerId],
          chance: Math.max(0, Math.min(100, chance)) // Limiter entre 0 et 100
        }
      }
    };
    setRules(newRules);
    
    // Sauvegarde différée
    if (rulesTimeoutRef.current) {
      clearTimeout(rulesTimeoutRef.current);
    }
    
    rulesTimeoutRef.current = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/lobby/rules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: lobbyCode, rules: newRules }),
        });
        
        if (socketContext) {
          socketContext.updateLobbyRules(lobbyCode, newRules);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des chances:', error);
      } finally {
        setIsModifyingRules(false);
      }
    }, 1000);
  };

  // Incrémente/Décrémente un paramètre numérique avec sauvegarde différée
  const updateRuleNumber = (
    name: 'categoriesToWin' | 'streakToWin',
    delta: number,
    min: number,
    max: number
  ) => {
    if (!isOwner) return;
    setIsModifyingRules(true);
    const currentVal = Number(rules[name] as number);
    const nextVal = Math.max(min, Math.min(max, currentVal + delta));
    if (nextVal === currentVal) {
      setIsModifyingRules(false);
      return;
    }
    const newRules = { ...rules, [name]: nextVal } as typeof rules;
    setRules(newRules);

    if (rulesTimeoutRef.current) {
      clearTimeout(rulesTimeoutRef.current);
    }

    rulesTimeoutRef.current = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/lobby/rules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: lobbyCode, rules: newRules }),
        });

        if (socketContext) {
          socketContext.updateLobbyRules(lobbyCode, newRules);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des règles (updateRuleNumber):', error);
      } finally {
        setIsModifyingRules(false);
      }
    }, 1000);
  };

  const handleStartGame = async () => {
    try {
      // Préparer les règles finales avec catégories sélectionnées
      const finalRules = {
        ...rules,
        selectedCategories: rules.selectedCategories && rules.selectedCategories.length > 0 ? rules.selectedCategories : undefined,
        mysteryAttackMode: rules.mysteryAttackMode || 'classic',
  difficultyMode: rules.difficultyMode || 'random',
        classicMalusValue: rules.classicMalusValue || 1,
  enableCategoryTimer: !!rules.enableCategoryTimer,
  categoryTimerSeconds: Number(rules.categoryTimerSeconds) || 15,
  enableQuestionTimer: !!rules.enableQuestionTimer,
  questionTimerSeconds: Number(rules.questionTimerSeconds) || 20,
  enableAntiCheat: rules.enableAntiCheat !== false,
  mysteryCooldownEnabled: rules.mysteryCooldownEnabled !== false,
  mysteryCooldownTurns: Number(rules.mysteryCooldownTurns) ?? 3,
        roulettePowers: rules.roulettePowers || defaultRules.roulettePowers
      };

      // Sauvegarder les règles finales
      await fetch(`${API_URL}/api/lobby/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: lobbyCode, rules: finalRules }),
      });
      
      // Préparer les données pour la partie multijoueur
      const multiplayerData = {
        lobbyCode,
        players: players.map((player: any, index: number) => ({
          id: index + 1,
          name: typeof player === 'string' ? player : player.name,
          uid: typeof player === 'string' ? '' : (player.uid || '')
        })),
        rules: {
          streakToWin: rules.streakToWin,
          categoriesToWin: rules.categoriesToWin,
          difficulty: rules.difficulty,
          difficultyMode: rules.difficultyMode || 'random',
          selectedCategories: rules.selectedCategories && rules.selectedCategories.length > 0 ? rules.selectedCategories : undefined,
          mysteryAttackMode: rules.mysteryAttackMode || 'classic',
          classicMalusValue: rules.classicMalusValue || 1,
          enableCategoryTimer: !!rules.enableCategoryTimer,
          categoryTimerSeconds: Number(rules.categoryTimerSeconds) || 15,
          enableQuestionTimer: !!rules.enableQuestionTimer,
          questionTimerSeconds: Number(rules.questionTimerSeconds) || 20,
          enableAntiCheat: rules.enableAntiCheat !== false,
          mysteryCooldownEnabled: rules.mysteryCooldownEnabled !== false,
          mysteryCooldownTurns: Number(rules.mysteryCooldownTurns) ?? 3,
          roulettePowers: rules.roulettePowers || defaultRules.roulettePowers
        },
        currentUser: user,
        hostName: isOwner ? user.displayName : (typeof players[0] === 'string' ? players[0] : players[0]?.name) // Si c'est l'owner, c'est lui l'hôte, sinon premier joueur
      };
      
      // Démarrer la partie multijoueur
      if (onStartMultiplayerGame) {
        onStartMultiplayerGame(multiplayerData);
      }
      
    } catch (error) {
      console.error("Erreur lors du démarrage de la partie:", error);
      alert("Erreur lors du démarrage de la partie. Veuillez réessayer.");
    }
  };

  return (
    <div className="lobby-room-bg">
      <div className="lobby-room-card">
        {/* En-tête avec code et actions */}
        <div className="lobby-room-header">
          <h2 className="lobby-room-title">
            {t('lobbyRoom.title')}
          </h2>
          
          {/* Contrôles dans l'en-tête */}
          <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
            <SoundControl size="sm" variant="compact" iconColor="var(--texte-clair)" />
          </div>
          
          {/* Zone du code avec feedback de copie au-dessus */}
          <div className="lobby-room-code-container">
            {copied && (
              <div className="copy-feedback">
                {t('lobbyRoom.copyFeedback')}
              </div>
            )}
            
            <div className="lobby-room-code-zone">
              <strong>{t('lobbyRoom.codeLabel')}</strong>
              <span 
                className={`lobby-room-code ${codeBlurred ? "blurred" : ""}`}
                onClick={handleCopy} 
                title={t('lobbyRoom.copyCodeTitle')}
              >
                {lobbyCode}
              </span>
              <button 
                className="lobby-room-toggle-visibility" 
                onClick={toggleCodeVisibility} 
                title={codeBlurred ? t('lobbyRoom.showCodeTitle') : t('lobbyRoom.hideCodeTitle')}
              >
                {codeBlurred ? "👁️" : "🔒"}
              </button>
            </div>
          </div>
        </div>

        {/* Section principale avec 2 colonnes */}
        <div className="lobby-room-main">
          
          {/* Colonne gauche - Joueurs */}
          <div className="lobby-room-players-section">
            <div className="section-header players-header">
              <h3 className="lobby-room-section-title">
                {t('lobbyRoom.playersTitle', { count: players.length })}
              </h3>
            </div>
            
            <div className="section-content players-content">
              <div className="lobby-room-players">
                {players.map((player, i) => {
                  const playerName = typeof player === 'string' ? player : player.name;
                  const playerUid = typeof player === 'string' ? '' : (player.uid || '');
                  const isConnected = typeof player === 'string' ? true : player.isConnected !== false;
                  
                  return (
                    <div
                      key={playerName}
                      className={`lobby-room-player${playerName === user.displayName ? ' me' : ''}${!isConnected ? ' disconnected' : ''}`}
                    >
                      <div
                        className="lobby-room-avatar"
                        style={{ 
                          background: PLAYER_COLORS[i % PLAYER_COLORS.length],
                          opacity: isConnected ? 1 : 0.5
                        }}
                      >
                        {playerName[0]?.toUpperCase() || '?'}
                        {i === 0 && <span className="lobby-room-crown">👑</span>}
                      </div>
                      <div className="player-info">
                        <span className="lobby-room-player-name">
                          <PlayerName
                            userId={playerUid}
                            userName={playerName}
                            checkEffect={!!playerUid}
                          />
                          {!isConnected && <span className="disconnected-label"> (Déconnecté)</span>}
                        </span>
                        {playerName === user.displayName && <span className="lobby-room-badge">{t('lobbyRoom.youBadge')}</span>}
                      </div>
                      {isOwner && playerName !== user.displayName && (
                        <button 
                          className="lobby-room-kick-btn" 
                          onClick={() => handleKick(playerName)} 
                          title={t('lobbyRoom.kickPlayerTitle')}
                        >
                          ✖
                        </button>
                      )}
                    </div>
                  );
                })}
                
                {/* Slots vides */}
                {Array.from({ length: 6 - players.length }).map((_, i) => (
                  <div key={i} className="lobby-room-player empty">
                    <div className="lobby-room-avatar empty">
                      +
                    </div>
                    <div className="player-info">
                      <span className="lobby-room-player-name">{t('lobbyRoom.emptySlot')}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bouton de lancement de partie ou message d'attente */}
              {isOwner && (
                <>
                  {players.length >= 2 ? (
                    <button className="lobby-room-save-btn" onClick={handleStartGame}>
                      {t('lobbyRoom.startGameButton', { count: players.length })}
                    </button>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '15px', 
                      marginTop: '15px',
                      color: '#8bb4f3',
                      fontStyle: 'italic',
                      backgroundColor: 'rgba(79, 140, 255, 0.1)',
                      borderRadius: '10px',
                      border: '1px solid rgba(79, 140, 255, 0.3)'
                    }}>
                      {t('lobbyRoom.waitingMessage')}
                      <br />
                      <small>{t('lobbyRoom.waitingCount', { current: players.length })}</small>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Colonne droite - Paramètres */}
          <div className="lobby-room-rules">
            <div className="section-header rules-header">
              <h3 className="lobby-room-section-title">
                {t('lobbyRoom.settingsTitle')}
              </h3>
            </div>
            
            <div className="section-content rules-content">
              <div className="lobby-room-rules-fields">
                
                {/* Paramètre Catégories */}
                <div className="rule-field">
                  <label>
                    <span className="rule-label">{t('lobbyRoom.categoriesToWin')}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                      <button
                        type="button"
                        onClick={() => updateRuleNumber('categoriesToWin', -1, 1, 6)}
                        disabled={!isOwner || (rules.categoriesToWin <= 1)}
                        title="Diminuer"
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(139, 180, 243, 0.3)',
                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                          color: 'var(--texte-principal)',
                          cursor: (!isOwner || rules.categoriesToWin <= 1) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        name="categoriesToWin"
                        min={1}
                        max={6}
                        value={rules.categoriesToWin}
                        onChange={isOwner ? handleRuleChange : undefined}
                        disabled={!isOwner}
                        style={{ width: '70px', textAlign: 'center' }}
                      />
                      <button
                        type="button"
                        onClick={() => updateRuleNumber('categoriesToWin', 1, 1, 6)}
                        disabled={!isOwner || (rules.categoriesToWin >= 6)}
                        title="Augmenter"
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(139, 180, 243, 0.3)',
                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                          color: 'var(--texte-principal)',
                          cursor: (!isOwner || rules.categoriesToWin >= 6) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        +
                      </button>
                    </div>
                  </label>
                </div>

                {/* Paramètre Streak */}
                <div className="rule-field">
                  <label>
                    <span className="rule-label">{t('lobbyRoom.streakToWin')}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                      <button
                        type="button"
                        onClick={() => updateRuleNumber('streakToWin', -1, 1, 10)}
                        disabled={!isOwner || (rules.streakToWin <= 1)}
                        title="Diminuer"
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(139, 180, 243, 0.3)',
                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                          color: 'var(--texte-principal)',
                          cursor: (!isOwner || rules.streakToWin <= 1) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        name="streakToWin"
                        min={1}
                        max={10}
                        value={rules.streakToWin}
                        onChange={isOwner ? handleRuleChange : undefined}
                        disabled={!isOwner}
                        style={{ width: '70px', textAlign: 'center' }}
                      />
                      <button
                        type="button"
                        onClick={() => updateRuleNumber('streakToWin', 1, 1, 10)}
                        disabled={!isOwner || (rules.streakToWin >= 10)}
                        title="Augmenter"
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(139, 180, 243, 0.3)',
                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                          color: 'var(--texte-principal)',
                          cursor: (!isOwner || rules.streakToWin >= 10) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        +
                      </button>
                    </div>
                  </label>
                </div>

                {/* Paramètre Difficulté */}
                <div className="rule-field difficulty-field">
                  <label>
                    <div className="difficulty-header">
                      <span className="rule-label">{t('lobbyRoom.difficultyLabel')}</span>
                      <span className="difficulty-value">
                        {(() => {
                          const labels = ['Très facile','Facile','Moyen','Difficile','Très difficile'];
                          const clamp = (v: number) => Math.max(1, Math.min(5, Math.floor(v)));
                          const [a,b] = rules.difficulty || [1,5];
                          return `${labels[clamp(a)-1]} → ${labels[clamp(b)-1]}`;
                        })()}
                      </span>
                    </div>
                    {/* Sélecteur du mode de difficulté */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <select
                        value={rules.difficultyMode || 'random'}
                        onChange={isOwner ? handleDifficultyModeChange : undefined}
                        disabled={!isOwner}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(139, 180, 243, 0.3)',
                          backgroundColor: isOwner ? 'rgba(139, 180, 243, 0.1)' : 'rgba(139, 180, 243, 0.05)',
                          color: 'var(--texte-principal)',
                          fontSize: '0.9rem',
                          cursor: isOwner ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <option value="random">🎲 Niveaux aléatoires (hérité)</option>
                        <option value="adaptive">🧠 Adaptatif (par catégorie)</option>
                        <option value="normal">📈 Progressif (selon streak)</option>
                      </select>
                    </div>
                    <div className="range-slider-container">
                      <RangeSlider
                        min={1}
                        max={5}
                        step={1}
                        value={rules.difficulty}
                        onChange={isOwner ? handleDifficultyChange : undefined}
                        isDisabled={!isOwner}
                      >
                        <RangeSliderTrack className="range-slider-track">
                          <RangeSliderFilledTrack className="range-slider-filled" />
                        </RangeSliderTrack>
                        <RangeSliderThumb className="range-slider-thumb" index={0} />
                        <RangeSliderThumb className="range-slider-thumb" index={1} />
                      </RangeSlider>
                    </div>
                  </label>
                </div>

                {/* Sélection des catégories */}
                <div className="rule-field">
                  <label>
                    <span className="rule-label">{t('lobbyRoom.categoriesSelection')}</span>
                    <div style={{ marginTop: '8px' }}>
                      {isOwner ? (
                        <button 
                          className="lobby-room-save-btn"
                          onClick={onCategorySelectorOpen}
                          style={{ 
                            width: '100%',
                            fontSize: '0.9rem',
                            padding: '8px 12px'
                          }}
                        >
                          {rules.selectedCategories.length > 0 
                            ? t('lobbyRoom.categoriesSelected', { count: rules.selectedCategories.length })
                            : t('lobbyRoom.selectCategoriesButton')}
                        </button>
                      ) : (
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                          border: '1px solid rgba(139, 180, 243, 0.3)',
                          borderRadius: '8px',
                          color: 'var(--texte-secondaire)',
                          fontSize: '0.9rem'
                        }}>
                          {rules.selectedCategories.length > 0 
                            ? t('lobbyRoom.categoriesSelected', { count: rules.selectedCategories.length })
                            : t('lobbyRoom.allCategories')}
                        </div>
                      )}
                    </div>
                  </label>
                  
                  {/* Affichage des catégories sélectionnées */}
                  {rules.selectedCategories.length > 0 && (
                    <div style={{
                      marginTop: '8px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px'
                    }}>
                      {rules.selectedCategories.map((category) => (
                        <span
                          key={category.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 6px',
                            backgroundColor: 'var(--bleu-electrique)',
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {category.emoji} {category.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mode d'attaque mystère */}
                <div className="rule-field">
                  <label>
                    <span className="rule-label">🎰 Mode d'attaque mystère</span>
                    <select
                      name="mysteryAttackMode"
                      value={rules.mysteryAttackMode || 'classic'}
                      onChange={isOwner ? handleRuleChange : undefined}
                      disabled={!isOwner}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        marginTop: '8px',
                        borderRadius: '8px',
                        border: '1px solid rgba(139, 180, 243, 0.3)',
                        backgroundColor: isOwner ? 'rgba(139, 180, 243, 0.1)' : 'rgba(139, 180, 243, 0.05)',
                        color: 'var(--texte-principal)',
                        fontSize: '0.9rem',
                        cursor: isOwner ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <option value="classic">⚔️ Classique (perte aléatoire)</option>
                      <option value="roulette">🎰 Roulette des pouvoirs</option>
                    </select>
                  </label>
                  
                  {/* Description du mode sélectionné */}
                  <div style={{
                    marginTop: '6px',
                    padding: '6px 8px',
                    backgroundColor: 'rgba(79, 140, 255, 0.1)',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    color: 'var(--texte-secondaire)',
                    lineHeight: '1.3'
                  }}>
                    {(rules.mysteryAttackMode || 'classic') === 'classic' ? (
                      <span>📝 Mode classique : Perte de {rules.classicMalusValue || 1} point{(rules.classicMalusValue || 1) > 1 ? 's' : ''} aléatoire{(rules.classicMalusValue || 1) > 1 ? 's' : ''} en cas d'échec</span>
                    ) : (
                      <span>🎰 Mode roulette : Sélection d'un pouvoir via une roulette visuelle avec différents effets</span>
                    )}
                  </div>
                </div>

                {/* Options avancées */}
                <div className="rule-field">
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                    <span className="rule-label">⚙️ Options avancées</span>
                    {isOwner && (
                      <button 
                        onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--bleu-electrique)',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(79, 140, 255, 0.1)'}
                        onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                      >
                        {showAdvancedOptions ? '▼ Masquer' : '▶ Afficher'}
                      </button>
                    )}
                  </div>

                  {/* Panneau des options avancées */}
                  {showAdvancedOptions && (
                    <div style={{
                      border: '1px solid rgba(139, 180, 243, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      backgroundColor: 'rgba(139, 180, 243, 0.05)',
                      marginTop: '8px'
                    }}>
                      {/* Timers */}
                      <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '0.9rem', 
                          color: 'var(--texte-principal)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          ⏱️ Timers
                        </h4>

                        {/* Timer Choix de Catégorie */}
                        <div style={{ marginLeft: '16px', marginBottom: '10px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={!!rules.enableCategoryTimer}
                              onChange={handleCategoryTimerToggle}
                              disabled={!isOwner}
                            />
                            <span style={{ fontSize: '0.85rem' }}>Activer le timer pour le choix de la catégorie</span>
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', marginLeft: '26px' }}>
                            <span style={{ fontSize: '0.85rem', minWidth: '110px', color: 'var(--texte-secondaire)' }}>Durée (s)</span>
                            <input
                              type="number"
                              min={5}
                              max={300}
                              value={Number(rules.categoryTimerSeconds) || 15}
                              onChange={handleCategoryTimerSecondsChange}
                              disabled={!isOwner || !rules.enableCategoryTimer}
                              style={{
                                width: '70px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid rgba(139, 180, 243, 0.3)',
                                backgroundColor: (isOwner && rules.enableCategoryTimer) ? 'rgba(139, 180, 243, 0.1)' : 'rgba(139, 180, 243, 0.05)',
                                color: 'var(--texte-principal)',
                                fontSize: '0.85rem',
                                textAlign: 'center'
                              }}
                            />
                          </div>
                        </div>

                        {/* Timer Réponse à la Question */}
                        <div style={{ marginLeft: '16px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={!!rules.enableQuestionTimer}
                              onChange={handleQuestionTimerToggle}
                              disabled={!isOwner}
                            />
                            <span style={{ fontSize: '0.85rem' }}>Activer le timer pour répondre à la question</span>
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', marginLeft: '26px' }}>
                            <span style={{ fontSize: '0.85rem', minWidth: '110px', color: 'var(--texte-secondaire)' }}>Durée (s)</span>
                            <input
                              type="number"
                              min={5}
                              max={300}
                              value={Number(rules.questionTimerSeconds) || 20}
                              onChange={handleQuestionTimerSecondsChange}
                              disabled={!isOwner || !rules.enableQuestionTimer}
                              style={{
                                width: '70px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid rgba(139, 180, 243, 0.3)',
                                backgroundColor: (isOwner && rules.enableQuestionTimer) ? 'rgba(139, 180, 243, 0.1)' : 'rgba(139, 180, 243, 0.05)',
                                color: 'var(--texte-principal)',
                                fontSize: '0.85rem',
                                textAlign: 'center'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Anti-triche */}
                      <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '0.9rem', 
                          color: 'var(--texte-principal)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          🛡️ Anti‑triche
                        </h4>
                        <div style={{ marginLeft: '16px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={rules.enableAntiCheat !== false}
                              onChange={handleAntiCheatToggle}
                              disabled={!isOwner}
                            />
                            <span style={{ fontSize: '0.85rem' }}>
                              Activer l'auto‑échec si un joueur change d'onglet pendant sa question
                            </span>
                          </label>
                          <div style={{ marginTop: '6px', marginLeft: '26px', fontSize: '0.8rem', color: 'var(--texte-secondaire)' }}>
                            Lorsque cette option est activée, quitter l'onglet ou la fenêtre pendant une question provoque automatiquement une réponse fausse pour le joueur actif.
                          </div>
                        </div>
                      </div>

                      {/* Mystère: Cooldown d'utilisation */}
                      <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '0.9rem', 
                          color: 'var(--texte-principal)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          🌀 Mystère — Cooldown
                        </h4>
                        <div style={{ marginLeft: '16px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={!!rules.mysteryCooldownEnabled}
                              onChange={handleMysteryCooldownToggle}
                              disabled={!isOwner}
                            />
                            <span style={{ fontSize: '0.85rem' }}>
                              Empêcher un joueur de réutiliser la catégorie Mystère pendant N tours après l'avoir choisie
                            </span>
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', marginLeft: '26px' }}>
                            <span style={{ fontSize: '0.85rem', minWidth: '140px', color: 'var(--texte-secondaire)' }}>Nombre de tours</span>
                            <input
                              type="number"
                              min={0}
                              max={10}
                              value={Number(rules.mysteryCooldownTurns) || 0}
                              onChange={handleMysteryCooldownTurnsChange}
                              disabled={!isOwner || !rules.mysteryCooldownEnabled}
                              style={{
                                width: '70px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid rgba(139, 180, 243, 0.3)',
                                backgroundColor: (isOwner && rules.mysteryCooldownEnabled) ? 'rgba(139, 180, 243, 0.1)' : 'rgba(139, 180, 243, 0.05)',
                                color: 'var(--texte-principal)',
                                fontSize: '0.85rem',
                                textAlign: 'center'
                              }}
                            />
                          </div>
                          <div style={{ marginTop: '6px', marginLeft: '26px', fontSize: '0.8rem', color: 'var(--texte-secondaire)' }}>
                            0 pour désactiver le cooldown. Par défaut: 3 tours.
                          </div>
                        </div>
                      </div>
                      
                      {/* Configuration mode classique */}
                      {(rules.mysteryAttackMode || 'classic') === 'classic' && (
                        <div style={{ marginBottom: '16px' }}>
                          <h4 style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '0.9rem', 
                            color: 'var(--texte-principal)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            ⚔️ Configuration Mode Classique
                          </h4>
                          
                          <div style={{ marginLeft: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '0.85rem', minWidth: '120px' }}>
                                💥 Malus appliqué :
                              </span>
                              <input
                                type="number"
                                min={1}
                                max={5}
                                value={rules.classicMalusValue || 1}
                                onChange={isOwner ? handleClassicMalusChange : undefined}
                                disabled={!isOwner}
                                style={{
                                  width: '60px',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  border: '1px solid rgba(139, 180, 243, 0.3)',
                                  backgroundColor: isOwner ? 'rgba(139, 180, 243, 0.1)' : 'rgba(139, 180, 243, 0.05)',
                                  color: 'var(--texte-principal)',
                                  fontSize: '0.85rem'
                                }}
                              />
                              <span style={{ fontSize: '0.8rem', color: 'var(--texte-secondaire)' }}>
                                point{(rules.classicMalusValue || 1) > 1 ? 's' : ''}
                              </span>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Configuration mode roulette */}
                      {rules.mysteryAttackMode === 'roulette' && (
                        <div>
                          <h4 style={{ 
                            margin: '0 0 12px 0', 
                            fontSize: '0.9rem', 
                            color: 'var(--texte-principal)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            🎰 Configuration Roulette des Pouvoirs
                          </h4>

                          {/* Pouvoirs Malus */}
                          <div style={{ marginBottom: '12px' }}>
                            <h5 style={{ 
                              margin: '0 0 6px 0', 
                              fontSize: '0.85rem', 
                              color: '#4F8CFF',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              🔵 Pouvoirs Malus
                            </h5>
                            {['minus1', 'minus2', 'minus3'].map((powerId) => {
                              const power = rules.roulettePowers[powerId];
                              const powerNames = {
                                minus1: 'Minus 1 (-1 point)',
                                minus2: 'Minus 2 (-2 points)', 
                                minus3: 'Minus 3 (-3 points)'
                              };
                              
                              return (
                                <div key={powerId} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '8px', 
                                  marginBottom: '4px',
                                  marginLeft: '12px'
                                }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px' }}>
                                    <input
                                      type="checkbox"
                                      checked={power.enabled}
                                      onChange={() => isOwner && handlePowerToggle(powerId)}
                                      disabled={!isOwner}
                                    />
                                    <span style={{ fontSize: '0.8rem' }}>{powerNames[powerId]}</span>
                                  </label>
                                  {power.enabled && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={power.chance}
                                        onChange={(e) => isOwner && handlePowerChanceChange(powerId, Number(e.target.value))}
                                        disabled={!isOwner}
                                        style={{
                                          width: '50px',
                                          padding: '2px 4px',
                                          borderRadius: '4px',
                                          border: '1px solid rgba(139, 180, 243, 0.3)',
                                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                                          color: 'var(--texte-principal)',
                                          fontSize: '0.8rem'
                                        }}
                                      />
                                      <span style={{ fontSize: '0.75rem', color: 'var(--texte-secondaire)' }}>%</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Pouvoirs Bonus */}
                          <div style={{ marginBottom: '12px' }}>
                            <h5 style={{ 
                              margin: '0 0 6px 0', 
                              fontSize: '0.85rem', 
                              color: '#4CAF50',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              🟢 Pouvoirs Bonus
                            </h5>
                            {['plus1', 'plus2', 'plus3'].map((powerId) => {
                              const power = rules.roulettePowers[powerId];
                              const powerNames = {
                                plus1: 'Plus 1 (+1 point)',
                                plus2: 'Plus 2 (+2 points)',
                                plus3: 'Plus 3 (+3 points)'
                              };
                              
                              return (
                                <div key={powerId} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '8px', 
                                  marginBottom: '4px',
                                  marginLeft: '12px'
                                }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px' }}>
                                    <input
                                      type="checkbox"
                                      checked={power.enabled}
                                      onChange={() => isOwner && handlePowerToggle(powerId)}
                                      disabled={!isOwner}
                                    />
                                    <span style={{ fontSize: '0.8rem' }}>{powerNames[powerId]}</span>
                                  </label>
                                  {power.enabled && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={power.chance}
                                        onChange={(e) => isOwner && handlePowerChanceChange(powerId, Number(e.target.value))}
                                        disabled={!isOwner}
                                        style={{
                                          width: '50px',
                                          padding: '2px 4px',
                                          borderRadius: '4px',
                                          border: '1px solid rgba(139, 180, 243, 0.3)',
                                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                                          color: 'var(--texte-principal)',
                                          fontSize: '0.8rem'
                                        }}
                                      />
                                      <span style={{ fontSize: '0.75rem', color: 'var(--texte-secondaire)' }}>%</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Pouvoirs Swap */}
                          <div>
                            <h5 style={{ 
                              margin: '0 0 6px 0', 
                              fontSize: '0.85rem', 
                              color: '#FF9800',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              🟠 Pouvoirs Swap
                            </h5>
                            {['swap1', 'swap2', 'swap3'].map((powerId) => {
                              const power = rules.roulettePowers[powerId];
                              const powerNames = {
                                swap1: 'Swap 1 (vole 1 point)',
                                swap2: 'Swap 2 (vole 2 points)',
                                swap3: 'Swap 3 (vole 3 points)'
                              };
                              
                              return (
                                <div key={powerId} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '8px', 
                                  marginBottom: '4px',
                                  marginLeft: '12px'
                                }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px' }}>
                                    <input
                                      type="checkbox"
                                      checked={power.enabled}
                                      onChange={() => isOwner && handlePowerToggle(powerId)}
                                      disabled={!isOwner}
                                    />
                                    <span style={{ fontSize: '0.8rem' }}>{powerNames[powerId]}</span>
                                  </label>
                                  {power.enabled && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={power.chance}
                                        onChange={(e) => isOwner && handlePowerChanceChange(powerId, Number(e.target.value))}
                                        disabled={!isOwner}
                                        style={{
                                          width: '50px',
                                          padding: '2px 4px',
                                          borderRadius: '4px',
                                          border: '1px solid rgba(139, 180, 243, 0.3)',
                                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                                          color: 'var(--texte-principal)',
                                          fontSize: '0.8rem'
                                        }}
                                      />
                                      <span style={{ fontSize: '0.75rem', color: 'var(--texte-secondaire)' }}>%</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Pouvoirs Spéciaux */}
                          <div style={{ marginTop: '12px' }}>
                            <h5 style={{ 
                              margin: '0 0 6px 0', 
                              fontSize: '0.85rem', 
                              color: '#9C27B0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              🟣 Pouvoirs Spéciaux
                            </h5>
                            {[
                              { id: 'blocked', label: 'Blocked (bloque une catégorie 3 tours)' },
                              { id: 'nuke', label: 'Nuke (réinitialise tous les progrès non-mystère)' },
                              { id: 'blind', label: 'Blind (aveugle 3 tours)' },
                              { id: 'megaswap', label: 'Megaswap (redistribue les progrès)' },
                              { id: 'translate', label: 'Translate (traduction 5 tours)' },
                              { id: 'hardcore', label: 'Hardcore (difficulté max 3 tours)' }
                            ].map(({ id, label }) => {
                              const power = rules.roulettePowers[id];
                              return (
                                <div key={id} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '8px', 
                                  marginBottom: '4px',
                                  marginLeft: '12px'
                                }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '260px' }}>
                                    <input
                                      type="checkbox"
                                      checked={power?.enabled}
                                      onChange={() => isOwner && handlePowerToggle(id)}
                                      disabled={!isOwner}
                                    />
                                    <span style={{ fontSize: '0.8rem' }}>{label}</span>
                                  </label>
                                  {power?.enabled && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={power?.chance ?? 10}
                                        onChange={(e) => isOwner && handlePowerChanceChange(id, Number(e.target.value))}
                                        disabled={!isOwner}
                                        style={{
                                          width: '50px',
                                          padding: '2px 4px',
                                          borderRadius: '4px',
                                          border: '1px solid rgba(139, 180, 243, 0.3)',
                                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                                          color: 'var(--texte-principal)',
                                          fontSize: '0.8rem'
                                        }}
                                      />
                                      <span style={{ fontSize: '0.75rem', color: 'var(--texte-secondaire)' }}>%</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Note explicative */}
                          <div style={{
                            marginTop: '12px',
                            padding: '8px',
                            backgroundColor: 'rgba(255, 193, 7, 0.1)',
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            color: 'var(--texte-secondaire)',
                            lineHeight: '1.3'
                          }}>
                            💡 <strong>Note :</strong> Les pourcentages indiquent la probabilité relative de chaque pouvoir. 
                            Le système normalisera automatiquement les valeurs pour que le total soit cohérent.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bouton de sortie centré */}
        <div className="lobby-room-footer">
          <button className="lobby-room-leave-btn" onClick={handleLeaveClick}>
            {t('lobbyRoom.leaveButton')}
          </button>
        </div>
      </div>
      
      {/* Modal de kick */}
      {kickedModal && (
        <div className="kicked-modal-overlay">
          <div className="kicked-modal">
            <h3>{t('lobbyRoom.kickedTitle')}</h3>
            <p>{t('lobbyRoom.kickedMessage')}</p>
            <button 
              onClick={() => {
                setKickedModal(false);
                onLeave();
              }}
            >
              {t('lobbyRoom.backToHomeButton')}
            </button>
          </div>
        </div>
      )}

      {/* Modal Lobby Non Trouvé */}
      {lobbyNotFoundModal && (
        <div className="modal-overlay">
          <div className="modal lobby-not-found-modal">
            <div className="modal-icon">
              <span className="error-icon">🚫</span>
            </div>
            <div className="modal-content">
              <h3 className="modal-title">Lobby introuvable</h3>
              <p className="modal-message">
                Oups ! Ce lobby n'existe pas ou a été supprimé.
              </p>
              <div className="modal-details">
                <span className="lobby-code">Code: {lobbyCode}</span>
              </div>
              <div className="countdown-container">
                <div className="countdown-circle">
                  <span className="countdown-text">{countdown}</span>
                </div>
                <p className="countdown-label">Redirection automatique...</p>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-primary"
                onClick={() => {
                  setLobbyNotFoundModal(false);
                  onLeave();
                }}
              >
                <span className="btn-icon">🏠</span>
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal CategorySelector */}
      <CategorySelector
        isOpen={isCategorySelectorOpen}
        onClose={onCategorySelectorClose}
        onCategoriesSelected={handleCategoriesSelected}
        initialSelectedCategories={rules.selectedCategories}
      />
    </div>
  );
};

export default LobbyRoomPage;
